import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../hooks/useRTL';

// Import icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import BuildIcon from '@mui/icons-material/Build';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import LogoutIcon from '@mui/icons-material/Logout';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ChatIcon from '@mui/icons-material/Chat';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SecurityIcon from '@mui/icons-material/Security';
import PermissionGuard from '../common/PermissionGuard';
import PERMISSIONS from '../../lib/permissions';


export default function Sidebar(props) {
  const { window } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isRTL, getIconPosition } = useRTL();

  const drawerWidth = {
    xs: '100%',
    sm: '260px',
    md: '16vw',
  };

  useEffect(() => {
    setSelectedItem(location.pathname);
  }, [location]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      localStorage.clear();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };


  const menuItems = [
    { name: t('navigation.dashboard'), to: '/dashboard', icon: <DashboardIcon /> },
    { name: t('navigation.requests'), to: '/requests', icon: <AssignmentIcon /> },
    { name: t('navigation.units'), to: '/units', icon: <ApartmentIcon />, required: [PERMISSIONS.UNIT.LIST, PERMISSIONS.UNIT.SHOW] },
    { name: t('navigation.owners'), to: '/owners', icon: <PeopleIcon />, required: [PERMISSIONS.OWNER.LIST, PERMISSIONS.OWNER.SHOW] },
    { name: t('navigation.admins'), to: '/admins', icon: <SupervisorAccountIcon />, required: [PERMISSIONS.ROLE.LIST] },
    { name: t('navigation.users'), to: '/users', icon: <PersonIcon />, required: [PERMISSIONS.CLIENT.LIST, PERMISSIONS.CLIENT.SHOW] },
    { name: t('navigation.bookingRequest'), to: '/booking', icon: <BookOnlineIcon />, required: [PERMISSIONS.BOOKING.LIST] },
    { name: t('navigation.maintenanceRequests'), to: '/maintenance', icon: <BuildIcon />, required: [PERMISSIONS.MAINTENANCE.LIST] },
    { name: t('navigation.cancellationRequests'), to: '/cancelrequest', icon: <DoDisturbOnIcon />, required: [PERMISSIONS.CANCELLATION_REQUEST.LIST] },
    { name: t('navigation.insuranceRefund'), to: '/insurancerefund', icon: <DoDisturbOnIcon />, required: [PERMISSIONS.REFUND_REQUEST.LIST] },
    { name: t('navigation.analysis'), to: '/analysis', icon: <AnalyticsIcon /> },
    { name: t('navigation.contracts'), to: '/contract', icon: <PictureAsPdfIcon />, required: [PERMISSIONS.CONTRACT.LIST] },
    { name: t('navigation.brokers'), to: '/brokers', icon: <SupervisorAccountIcon />, required: [PERMISSIONS.BROKER.LIST] },
    { name: t('navigation.brokerPayment'), to: '/brokerpayment', icon: <SupervisorAccountIcon />, required: [PERMISSIONS.BROKER_PAYMENT.LIST] },
    { name: t('navigation.ownersBankAccounts'), to: '/bankowners', icon: <AccountBalanceIcon />, required: [PERMISSIONS.BANK_ACCOUNT.LIST] },
    { name: t('navigation.servicesRequests'), to: '/servicereq', icon: <DesignServicesIcon />, required: [PERMISSIONS.SERVICE_REQUEST.LIST] },
    { name: t('navigation.profile'), to: '/profile', icon: <AccountBoxIcon /> },
    { name: t('navigation.chats'), to: '/chats', icon: <ChatIcon />, required: [PERMISSIONS.CHAT.LIST] },
    { name: t('navigation.notification'), to: '/notification', icon: <NotificationsIcon />, required: [PERMISSIONS.NOTIFICATION.LIST] },
    { name: t('navigation.rolesPermissions'), to: '/roles', icon: <SecurityIcon />, required: [PERMISSIONS.ROLE.LIST] },
    { name: t('navigation.settings'), to: '/settings', icon: <BuildIcon />, required: [
      PERMISSIONS.SETTING.LIST,
      PERMISSIONS.BANK.LIST,
      PERMISSIONS.COMPOUND.LIST,
      PERMISSIONS.FEATURE.LIST,
      PERMISSIONS.SERVICE.LIST,
      PERMISSIONS.COUNTRY.LIST,
      PERMISSIONS.CITY.LIST,
      PERMISSIONS.UNIT_TYPE.LIST,
      PERMISSIONS.PLACE_TYPE.LIST,
      PERMISSIONS.IDENTITY_TYPE.LIST,
      PERMISSIONS.PAYMENT_METHOD.LIST,
      PERMISSIONS.PROMO_CODE.LIST,
      PERMISSIONS.MAINTENANCE_SERVICE.LIST,
      PERMISSIONS.SOCIAL_MEDIA.LIST,
      PERMISSIONS.SETTING_TYPE.LIST
    ], requireAll: false }, // Show Settings tab if user has ANY settings permission
    { name: t('navigation.contactUs'), to: '/contactus', icon: <ContactMailIcon />, required: [PERMISSIONS.CONTACT_US.LIST] },
    { name: t('navigation.logout'), icon: <LogoutIcon />, onClick: handleLogout },
  ];

  const handleItemClick = (path) => {
    setSelectedItem(path);
    setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      backgroundColor: "#ffffff",
      overflow: 'hidden'
    }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          p: { xs: 2, md: 3 },
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}
      >
        <img 
          alt="Logo" 
          src="/assets/Bask-logo.png" 
          style={{ 
            height: 'auto', 
            width: 'auto',
            maxWidth: '80%',
            maxHeight: '64px',
            display: 'block'
          }}
          onError={(e) => {
            console.warn('Logo image failed to load, showing text fallback');
            e.target.style.display = 'none';
            
            // Only add text fallback if it doesn't already exist
            if (!e.target.parentNode.querySelector('.logo-text-fallback')) {
              const textFallback = document.createElement('div');
              textFallback.className = 'logo-text-fallback';
              textFallback.textContent = 'LOGO';
              textFallback.style.cssText = `
                height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                color: #C47D60;
                font-size: 18px;
              `;
              e.target.parentNode.appendChild(textFallback);
            }
          }}
          onLoad={(e) => {
            // Logo loaded successfully - remove any text fallback
            const textFallback = e.target.parentNode.querySelector('.logo-text-fallback');
            if (textFallback) {
              textFallback.remove();
            }
          }}
        />
      </Box>

      <List 
        sx={{ 
          flexGrow: 1, 
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#C47D60',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#A0664A',
          },
          msOverflowStyle: 'none'
        }}>
        {menuItems.map((item, index) => (
          <React.Fragment key={index}>
            {/* Always show Dashboard, Profile, and Logout, check permissions for others */}
            {item.name === t('navigation.dashboard') || item.name === t('navigation.profile') || item.name === t('navigation.logout') ? (
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={item.to ? Link : 'button'}
                  to={item.to || undefined}
                  onClick={() => {
                    if (item.onClick) {
                      item.onClick();
                    } else {
                      handleItemClick(item.to);
                    }
                  }}
                  sx={{
                    width: '100%',
                    backgroundColor: selectedItem === item.to ? '#C47D60' : 'transparent',
                    '&:hover': {
                      backgroundColor: selectedItem === item.to 
                        ? '#C47D60' 
                        : 'rgba(196, 125, 96, 0.08)'
                    },
                    py: { xs: 0.75, sm: 1, md: 1.25 },
                    px: { xs: 1, sm: 1.5, md: 2 },
                    display: 'flex',
                    flexDirection: isRTL ? 'row-reverse' : 'row',
                    gap: { xs: 1, md: 1.5 },
                    justifyContent: isRTL ? 'flex-end' : 'flex-start',
                    '& .MuiListItemIcon-root': {
                      minWidth: { xs: 36, sm: 40, md: 48 },
                      color: selectedItem === item.to ? 'white' : 'inherit',
                    },
                    '& .MuiListItemText-root': {
                      textAlign: isRTL ? 'right' : 'left',
                    },
                    '& .MuiListItemText-primary': {
                      fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                      fontWeight: selectedItem === item.to ? 600 : 400,
                      color: selectedItem === item.to ? 'white' : 'inherit',
                    }
                  }}
                >
                  {getIconPosition(<ListItemIcon>{item.icon}</ListItemIcon>, <ListItemText primary={item.name} />)}
                </ListItemButton>
              </ListItem>
            ) : (
              <PermissionGuard 
                required={item.required || []} 
                fallback={null}
                requireAll={item.requireAll || false}
                silent={true}
              >
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    component={item.to ? Link : 'button'}
                    to={item.to || undefined}
                    onClick={() => {
                      if (item.onClick) {
                        item.onClick();
                      } else {
                        handleItemClick(item.to);
                      }
                    }}
                    sx={{
                      width: '100%',
                      backgroundColor: selectedItem === item.to ? '#C47D60' : 'transparent',
                      '&:hover': {
                        backgroundColor: selectedItem === item.to 
                          ? '#C47D60' 
                          : 'rgba(196, 125, 96, 0.08)'
                      },
                      py: { xs: 0.75, sm: 1, md: 1.25 },
                      px: { xs: 1, sm: 1.5, md: 2 },
                      display: 'flex',
                      flexDirection: isRTL ? 'row-reverse' : 'row',
                      gap: { xs: 1, md: 2 },
                      justifyContent: isRTL ? 'flex-end' : 'flex-start',
                      '& .MuiListItemIcon-root': {
                        minWidth: { xs: 36, sm: 40, md: 48 },
                        color: selectedItem === item.to ? 'white' : 'inherit',
                      },
                      '& .MuiListItemText-root': {
                        textAlign: isRTL ? 'right' : 'left',
                      },
                      '& .MuiListItemText-primary': {
                        fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                        fontWeight: selectedItem === item.to ? 600 : 400,
                        color: selectedItem === item.to ? 'white' : 'inherit',
                      }
                    }}
                  >
                    {getIconPosition(<ListItemIcon>{item.icon}</ListItemIcon>, <ListItemText primary={item.name} />)}
                  </ListItemButton>
                </ListItem>
              </PermissionGuard>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth.md})` },
          [isRTL ? 'mr' : 'ml']: { md: drawerWidth.md },
          display: { md: 'none' },
          backgroundColor: '#C47D60',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth.md }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth.xs,
              backgroundColor: '#ffffff',
              [isRTL ? 'right' : 'left']: 0
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth.md,
              backgroundColor: '#ffffff',
              position: 'fixed',
              [isRTL ? 'right' : 'left']: 0,
              top: 0,
              height: '100vh'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
    </Box>
  );
}